import React, { useEffect, useState } from "react";
import { Panel } from "@/components/common/Panel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { odrApi, odrErr, label } from "@/components/odr/odrApi";
import { cn } from "@/lib/utils";
import { Lock, Users, Send } from "lucide-react";

export function MessagesTab({ c }) {
  const [threads, setThreads] = useState([]);
  const [thread, setThread] = useState(null);
  const [rows, setRows] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const loadThreads = () => odrApi.threads(c.id).then((t) => { setThreads(t); setThread((cur) => cur || t[0]?.key || null); }).catch(odrErr);
  const load = () => thread && odrApi.messages(c.id, thread).then(setRows).catch(odrErr);
  useEffect(() => { loadThreads(); }, [c.id]); // eslint-disable-line
  useEffect(() => { load(); const t = setInterval(() => { load(); loadThreads(); }, 8000); return () => clearInterval(t); }, [thread]); // eslint-disable-line
  const send = async () => {
    setBusy(true);
    try { await odrApi.sendMessage(c.id, { thread, text }); setText(""); load(); loadThreads(); } catch (e) { odrErr(e); } finally { setBusy(false); }
  };
  const cur = threads.find((t) => t.key === thread);
  const priv = thread?.endsWith("_NEUTRAL");
  const me = c.me;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-4" data-testid="odr-messages">
      <Panel className="p-2 lg:col-span-1">
        {threads.map((t) => (
          <button key={t.key} onClick={() => setThread(t.key)} data-testid={`thread-${t.key}`}
            className={cn("flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm", thread === t.key ? "bg-navy text-white" : "text-ink-2 hover:bg-surface")}>
            <span className="flex items-center gap-2">{t.key.endsWith("_NEUTRAL") ? <Lock className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />}{t.label}</span>
            {t.unread > 0 && <span className="rounded-full bg-copper px-1.5 text-[10px] font-semibold text-white" data-testid={`thread-unread-${t.key}`}>{t.unread}</span>}
          </button>
        ))}
        {!threads.length && <div className="p-3 text-xs text-ink-muted">No message threads available for your role.</div>}
      </Panel>
      <div className="space-y-3 lg:col-span-3">
        {cur && <div className={cn("flex items-center gap-2 rounded-lg px-3 py-2 text-xs", priv ? "bg-warning-bg text-warning" : "bg-info-bg text-info")} data-testid="messages-visibility"><Lock className="h-3.5 w-3.5" /> VISIBLE TO: {cur.label} — enforced server-side. {priv ? "The other side can never read this thread." : "Every party and the neutral read this thread."}</div>}
        <Panel className="flex h-[440px] flex-col p-3">
          <div className="nyk-scroll flex-1 space-y-2 overflow-y-auto">
            {rows.map((m) => {
              const mine = m.authorId === me.userId || m.authorName === me.name;
              return (
                <div key={m.id} className={cn("max-w-[80%] rounded-lg px-3 py-2 text-sm", mine ? "ml-auto bg-copper-light text-ink" : "bg-surface text-ink")} data-testid={`msg-${m.id}`}>
                  <div className="text-[10px] text-ink-muted">{m.authorName} · {label(m.authorRole)} · {String(m.createdAt).slice(0, 16).replace("T", " ")}</div>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
              );
            })}
            {!rows.length && <div className="p-6 text-center text-xs text-ink-muted">No messages yet in this thread.</div>}
          </div>
          <div className="mt-3 flex gap-2">
            <Textarea rows={2} className="text-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder={cur ? `Message ${cur.label}…` : "Select a thread"} data-testid="message-text" />
            <Button size="sm" onClick={send} disabled={busy || !text.trim() || !thread} data-testid="message-send-btn"><Send className="h-4 w-4" /></Button>
          </div>
        </Panel>
      </div>
    </div>
  );
}
